import { getApiClient } from '@src/shared/types/api-client'
import { DeadLetterFiltersDto, DeadLetterLog, DeadLetterSubscriptionSummary, DiscardDeadLettersDto, RetriggerDeadLettersDto } from '@src/tools/dead-letter/types/Constants'
import { MutationFunction, QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const SCOPE = 'dead-letters'
const LIST = 'list'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  list: (filters: DeadLetterFiltersDto) => [{ scope: SCOPE, entity: LIST, ...filters }] as const,
  allLists: () => [{ scope: SCOPE, entity: LIST }] as const,
  subscriptions: () => [{ scope: SCOPE, entity: 'subscriptions' }] as const,
}

const getDeadLettersList = async ({
  queryKey: [filters],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['list']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('DeadLetterLogs/Search', filters)
  return response.data as DeadLetterLog[]
}

export function useDeadLettersList(filters: DeadLetterFiltersDto, enabled = true): [DeadLetterLog[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.list(filters)],
    queryFn: getDeadLettersList,
    placeholderData: [],
    enabled,
  })

  return [data ?? [], isFetching]
}

const getDeadLetterSubscriptions = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.get('DeadLetterLogs/SubscriptionNames')
  return response.data as DeadLetterSubscriptionSummary[]
}

export function useDeadLetterSubscriptions(enabled = true): [DeadLetterSubscriptionSummary[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.subscriptions()],
    queryFn: getDeadLetterSubscriptions,
    placeholderData: [],
    enabled,
  })

  return [data ?? [], isFetching]
}

const retriggerDeadLetters: MutationFunction<{ retriggeredCount: number }, RetriggerDeadLettersDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('DeadLetterLogs/Retrigger', dto)
  return response.data as { retriggeredCount: number }
}

export function useRetriggerDeadLetters(): [
  MutationFunction<{ retriggeredCount: number }, RetriggerDeadLettersDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: retriggerDeadLetters,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      await queryClient.invalidateQueries({ queryKey: keysFactory.subscriptions() })
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

const discardDeadLetters: MutationFunction<{ discardedCount: number }, DiscardDeadLettersDto> = async (dto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('DeadLetterLogs/Discard', dto)
  return response.data as { discardedCount: number }
}

export function useDiscardDeadLetters(): [
  MutationFunction<{ discardedCount: number }, DiscardDeadLettersDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: discardDeadLetters,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      await queryClient.invalidateQueries({ queryKey: keysFactory.subscriptions() })
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

const clearDeadLetters: MutationFunction<{ clearedCount: number }, DeadLetterFiltersDto> = async (filters) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('DeadLetterLogs/Clear', filters)
  return response.data as { clearedCount: number }
}

export function useClearDeadLetters(): [
  MutationFunction<{ clearedCount: number }, DeadLetterFiltersDto>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: clearDeadLetters,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allLists() })
      await queryClient.invalidateQueries({ queryKey: keysFactory.subscriptions() })
    },
  })

  return [mutateAsync, isPending, isError, reset]
}
